/**
 * VehicleDetail Page
 * 
 * Modern, premium vehicle detail view with telemetry
 */

import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTheme } from '../../../context/ThemeContext';
import { useVehicleLiveTelemetry } from '../../../hooks/useTelemetry';
import api from '../../../lib/api';
import { 
  ArrowLeft, 
  Fuel, 
  Battery, 
  Gauge, 
  MapPin, 
  Activity, 
  Radio, 
  Settings, 
  ShieldCheck, 
  Clock, 
  Navigation,
  ExternalLink,
  LayoutGrid,
  X,
  Upload,
  Image as ImageIcon
} from 'lucide-react';
import { LineChart, Line, ResponsiveContainer } from 'recharts';
import GoogleMapView from '../../../components/GoogleMapView';

// Generate mock history data for speed chart
const generateSpeedHistory = (currentSpeed = 0) => {
  return Array.from({ length: 20 }, (_, i) => ({
    time: i,
    speed: Math.max(0, currentSpeed + (Math.random() * 20 - 10)),
  }));
};

export default function VehicleDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isDark } = useTheme();
  
  const [vehicle, setVehicle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [latestTelemetry, setLatestTelemetry] = useState(null);
  const [activeTab, setActiveTab] = useState('status');
  const [speedHistory, setSpeedHistory] = useState([]);
  const [showEditModal, setShowEditModal] = useState(false);
  
  // Subscribe to live telemetry for this vehicle
  const { telemetry: liveTelemetry, lastUpdate, connected } = useVehicleLiveTelemetry(vehicle?.imei);

  // Load vehicle data
  useEffect(() => {
    loadVehicle();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadVehicle = async () => {
    try {
      setLoading(true);
      const data = await api.get(`/api/vehicles/${id}`);
      setVehicle(data);
      
      // Load latest telemetry from database
      if (data.imei) {
        try {
          const telemetryData = await api.get(`/api/telemetry/vehicle/${id}/latest`);
          setLatestTelemetry(telemetryData);
          setSpeedHistory(generateSpeedHistory(telemetryData?.speed_mph || 0));
        } catch (telemetryErr) {
          console.log('No telemetry data available yet');
          setSpeedHistory(generateSpeedHistory(0));
        }
      }
    } catch (err) {
      console.error('Failed to load vehicle:', err);
    } finally {
      setLoading(false);
    }
  };
  
  // Update telemetry with live data
  useEffect(() => {
    if (liveTelemetry) {
      setLatestTelemetry(liveTelemetry);
      setSpeedHistory(generateSpeedHistory(liveTelemetry?.speed_mph || 0));
    }
  }, [liveTelemetry]);

  if (loading) {
    return (
      <div className="h-screen bg-slate-950 flex flex-col items-center justify-center space-y-4">
        <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
        <p className="text-slate-400 font-medium animate-pulse">Initializing Telemetry...</p>
      </div>
    );
  }

  if (!vehicle) {
    return (
      <div className="h-screen bg-slate-950 flex flex-col items-center justify-center space-y-4">
        <p className="text-slate-400 font-medium">Vehicle not found</p>
        <button
          onClick={() => navigate('/vehicles')}
          className="px-5 py-2.5 bg-blue-600 hover:bg-blue-500 text-white rounded-xl font-medium transition-all"
        >
          Back to Vehicles
        </button>
      </div>
    );
  }

  const isLive = connected && liveTelemetry;
  const telemetry = latestTelemetry;
  const currentSpeed = telemetry?.speed_mph || 0;
  const lat = telemetry?.lat || vehicle.current_lat;
  const lon = telemetry?.lon || vehicle.current_lng;

  return (
    <div className="h-screen w-screen bg-slate-950 text-slate-100 flex flex-col overflow-hidden relative">
      {/* Background Glows */}
      <div className="absolute top-1/4 left-1/4 w-[500px] h-[500px] bg-blue-600/10 blur-[120px] rounded-full -z-10 animate-pulse"></div>
      <div className="absolute bottom-1/4 right-1/4 w-[400px] h-[400px] bg-indigo-600/10 blur-[100px] rounded-full -z-10"></div>

      {/* Navigation Header */}
      <header className="px-8 py-6 flex items-center justify-between z-20 shrink-0">
        <div className="flex items-center gap-6">
          <button 
            onClick={() => navigate('/vehicles')}
            className="p-2 hover:bg-slate-800/50 rounded-full transition-colors text-slate-400 hover:text-white"
          >
            <ArrowLeft className="w-6 h-6" />
          </button>
          <div>
            <h1 className="text-2xl font-bold tracking-tight">
              {vehicle.vehicle_name || `${vehicle.year} ${vehicle.make} ${vehicle.model}`}
            </h1>
            <div className="flex items-center gap-3 mt-1 text-sm font-mono text-slate-500">
              {vehicle.vehicle_name && (
                <>
                  <span>{vehicle.year} {vehicle.make} {vehicle.model}</span>
                  <span className="w-1 h-1 bg-slate-700 rounded-full"></span>
                </>
              )}
              <span className="text-blue-400">{vehicle.license_plate || vehicle.vin?.slice(-8)}</span>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-4">
          <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full border text-xs font-semibold ${isLive ? 'bg-green-500/10 border-green-500/20 text-green-400' : 'bg-slate-800 border-slate-700 text-slate-400'}`}>
            <Radio className={`w-3.5 h-3.5 ${isLive ? 'animate-pulse' : ''}`} />
            {isLive ? 'LIVE TELEMETRY' : 'OFFLINE'}
          </div>
          <button 
            onClick={() => setShowEditModal(true)}
            className="p-2.5 hover:bg-slate-800/50 rounded-xl transition-colors border border-slate-800 text-slate-400"
          >
            <Settings className="w-5 h-5" />
          </button>
          <button 
            onClick={() => navigate('/vehicles/live')}
            className="flex items-center gap-2 px-4 py-2 bg-slate-800 hover:bg-slate-700 rounded-full text-sm font-semibold transition-colors border border-slate-700"
          >
            <MapPin className="w-4 h-4" />
            View on Map
          </button>
        </div>
      </header>

      {/* Main Content Area */}
      <main className="flex-1 relative flex flex-col lg:flex-row p-8 gap-8 overflow-y-auto min-h-0">
        
        {/* Left Stats Column */}
        <aside className="w-full lg:w-80 flex flex-col gap-6 z-10">
          <div className="bg-slate-900/40 backdrop-blur-xl border border-slate-800 p-6 rounded-3xl">
            <h3 className="text-slate-500 text-xs font-bold uppercase tracking-widest mb-4 flex items-center gap-2">
              <Activity className="w-3.5 h-3.5 text-blue-500" />
              Core Metrics
            </h3>
            <div className="space-y-6">
              <MetricItem 
                icon={<Fuel className="w-4 h-4" />} 
                label="Fuel Level" 
                value={`${vehicle.fuel_level || 0}%`} 
                subValue={vehicle.fuel_level > 50 ? 'Good' : 'Low'} 
                color="text-emerald-400" 
              />
              <MetricItem 
                icon={<Battery className="w-4 h-4" />} 
                label="Battery" 
                value={`${vehicle.battery_level || 0}%`} 
                subValue={vehicle.battery_level > 80 ? 'Excellent' : 'Fair'} 
                color="text-blue-400" 
              />
              <MetricItem 
                icon={<Gauge className="w-4 h-4" />} 
                label="Odometer" 
                value={`${((vehicle.current_odometer || 0) / 1000).toFixed(1)}k mi`} 
                subValue={telemetry ? 'Tracking' : 'Idle'} 
                color="text-indigo-400" 
              />
            </div>
          </div>

          <div className="bg-slate-900/40 backdrop-blur-xl border border-slate-800 p-6 rounded-3xl flex-1">
            <h3 className="text-slate-500 text-xs font-bold uppercase tracking-widest mb-4 flex items-center gap-2">
              <Clock className="w-3.5 h-3.5 text-blue-500" />
              Recent Activity
            </h3>
            <div className="space-y-4">
              {isLive && <ActivityLogItem time="Live" label="Telemetry Active" type="success" />}
              {telemetry && <ActivityLogItem time={lastUpdate ? `${Math.floor((new Date() - lastUpdate) / 1000)}s ago` : 'Now'} label="Location Updated" type="neutral" />}
              {vehicle.status === 'active' && <ActivityLogItem time="Today" label="Vehicle Active" type="success" />}
              {vehicle.tire_pressure_fl < 30 && <ActivityLogItem time="Recent" label="Low Tire Pressure" type="warning" />}
            </div>
          </div>
        </aside>

        {/* Center Vehicle Section */}
        <section className="flex-1 flex flex-col items-center justify-center relative min-h-[400px]">
          {/* Main Car Image */}
          <div className="relative group transition-all duration-700 hover:scale-105">
            <div className="absolute -inset-1 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-full blur opacity-20 group-hover:opacity-40 transition duration-1000"></div>
            <img 
              src={vehicle.vehicle_image_url || "https://images.unsplash.com/photo-1503376780353-7e6692767b70?auto=format&fit=crop&q=80&w=1000"} 
              alt="Vehicle Hero" 
              className="relative w-full max-w-2xl h-auto drop-shadow-[0_35px_35px_rgba(0,0,0,0.5)] transform -scale-x-100"
            />
            
            {/* Overlay Points */}
            <Hotspot x="20%" y="40%" label="Front System" />
            <Hotspot x="50%" y="80%" label="Engine/Motor" />
            <Hotspot x="80%" y="45%" label="Cargo Space" />
          </div>

          {/* Speed Indicator Floating */}
          <div className="absolute top-0 right-0 lg:right-10 bg-slate-900/60 backdrop-blur-md border border-slate-700/50 p-6 rounded-3xl text-center min-w-[160px] shadow-2xl">
            <span className="text-slate-500 text-xs font-bold uppercase tracking-widest block mb-2">
              {isLive ? 'Live Speed' : 'Last Speed'}
            </span>
            <div className="flex items-baseline justify-center gap-1">
              <span className="text-5xl font-black text-white">{Math.round(currentSpeed)}</span>
              <span className="text-slate-400 font-medium">mph</span>
            </div>
            <div className="mt-4 h-8 w-full">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={speedHistory}>
                  <Line 
                    type="monotone" 
                    dataKey="speed" 
                    stroke="#3b82f6" 
                    strokeWidth={2} 
                    dot={false} 
                    isAnimationActive={true} 
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Floor Reflection/Shadow */}
          <div className="w-[80%] h-12 bg-black/40 blur-2xl rounded-[100%] absolute bottom-20 -z-10"></div>
        </section>

        {/* Right Info Column */}
        <aside className="w-full lg:w-80 flex flex-col gap-6 z-10">
          <div 
            className="bg-slate-900/40 backdrop-blur-xl border border-slate-800 p-6 rounded-3xl overflow-hidden relative group"
          >
            <div className="absolute top-0 right-0 p-3 z-10">
              <button
                onClick={() => navigate('/vehicles/live')}
                className="p-2 hover:bg-slate-800/50 rounded-lg transition-colors"
              >
                <ExternalLink className="w-4 h-4 text-slate-500 group-hover:text-blue-400 transition-colors" />
              </button>
            </div>
            <h3 className="text-slate-500 text-xs font-bold uppercase tracking-widest mb-4 flex items-center gap-2">
              <Navigation className="w-3.5 h-3.5 text-blue-500" />
              Live Location
            </h3>
            <div className="aspect-square w-full rounded-2xl bg-slate-800 overflow-hidden relative border border-slate-700 mb-4">
              {lat && lon ? (
                <div className="w-full h-full relative">
                  <GoogleMapView
                    center={{ lat: parseFloat(lat), lng: parseFloat(lon) }}
                    zoom={15}
                    assets={[{
                      id: vehicle.id,
                      lat: parseFloat(lat),
                      lng: parseFloat(lon),
                      name: vehicle.vehicle_name || `${vehicle.year} ${vehicle.make} ${vehicle.model}`,
                      type: 'Vehicle',
                      assetType: 'vehicle',
                      status: vehicle.status || 'active',
                      live: !!telemetry,
                      imei: vehicle.imei
                    }]}
                    mapTypeId="hybrid"
                  />
                  {/* Overlay button */}
                  <div className="absolute bottom-2 left-1/2 transform -translate-x-1/2 z-10">
                    <button
                      onClick={() => navigate('/vehicles/live')}
                      className="px-3 py-1.5 bg-blue-600/90 hover:bg-blue-500 text-white text-xs font-semibold rounded-lg transition-colors shadow-lg backdrop-blur-sm"
                    >
                      View Full Map
                    </button>
                  </div>
                </div>
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <MapPin className="w-12 h-12 text-slate-600" />
                </div>
              )}
            </div>
            {lat && lon && (
              <p className="text-xs font-mono text-slate-400 truncate">
                {parseFloat(lat).toFixed(4)}° N, {parseFloat(lon).toFixed(4)}° E
              </p>
            )}
          </div>

          <div className="bg-slate-900/40 backdrop-blur-xl border border-slate-800 p-6 rounded-3xl flex-1">
            <h3 className="text-slate-500 text-xs font-bold uppercase tracking-widest mb-6 flex items-center gap-2">
              <ShieldCheck className="w-3.5 h-3.5 text-blue-500" />
              Safety & Tires
            </h3>
            <div className="grid grid-cols-2 gap-4">
              <TireBox label="F. Left" psi={vehicle.tire_pressure_fl || 0} />
              <TireBox label="F. Right" psi={vehicle.tire_pressure_fr || 0} />
              <TireBox label="R. Left" psi={vehicle.tire_pressure_rl || 0} />
              <TireBox label="R. Right" psi={vehicle.tire_pressure_rr || 0} />
            </div>
            <div className="mt-6 p-4 rounded-2xl bg-slate-800/50 border border-slate-700/50">
              <div className="flex items-center justify-between text-sm">
                <span className="text-slate-400">Vehicle Status</span>
                <span className={`font-semibold ${vehicle.status === 'active' ? 'text-emerald-400' : 'text-slate-400'}`}>
                  {vehicle.status ? vehicle.status.charAt(0).toUpperCase() + vehicle.status.slice(1) : 'Unknown'}
                </span>
              </div>
            </div>
          </div>
        </aside>
      </main>

      {/* Edit Vehicle Modal */}
      {showEditModal && vehicle && (
        <EditVehicleModal 
          vehicle={vehicle}
          onClose={() => setShowEditModal(false)}
          onSuccess={() => {
            loadVehicle();
            setShowEditModal(false);
          }}
        />
      )}
    </div>
  );
}

// --- Helper Components ---

const MetricItem = ({ icon, label, value, subValue, color }) => (
  <div className="flex items-center gap-4 group cursor-default">
    <div className={`p-3 rounded-2xl bg-slate-800 border border-slate-700 ${color} transition-transform group-hover:scale-110`}>
      {icon}
    </div>
    <div className="flex-1">
      <p className="text-slate-500 text-xs font-medium">{label}</p>
      <div className="flex items-center justify-between">
        <span className="text-lg font-bold text-white leading-tight">{value}</span>
        <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${color.replace('text', 'bg').replace('400', '400/10')} ${color}`}>
          {subValue}
        </span>
      </div>
    </div>
  </div>
);

const ActivityLogItem = ({ time, label, type }) => {
  const dotColor = type === 'success' ? 'bg-emerald-500' : type === 'warning' ? 'bg-amber-500' : 'bg-blue-500';
  return (
    <div className="flex items-center gap-3">
      <div className={`w-1.5 h-1.5 rounded-full ${dotColor}`}></div>
      <span className="text-sm font-medium text-slate-300 flex-1">{label}</span>
      <span className="text-[10px] font-mono text-slate-500">{time}</span>
    </div>
  );
};

const TireBox = ({ label, psi }) => (
  <div className={`p-3 rounded-2xl border ${psi < 30 ? 'bg-amber-500/5 border-amber-500/20' : 'bg-slate-800/40 border-slate-700/50'}`}>
    <p className="text-[10px] font-bold text-slate-500 uppercase tracking-tighter mb-1">{label}</p>
    <div className="flex items-baseline gap-1">
      <span className={`text-lg font-bold ${psi < 30 ? 'text-amber-400' : 'text-white'}`}>{psi || 0}</span>
      <span className="text-xs text-slate-500 font-medium">PSI</span>
    </div>
  </div>
);

const Hotspot = ({ x, y, label }) => (
  <div 
    className="absolute group z-10" 
    style={{ left: x, top: y }}
  >
    <div className="relative">
      <div className="w-4 h-4 bg-white/30 rounded-full border border-white/50 animate-pulse cursor-pointer"></div>
      <div className="absolute top-0 left-6 bg-slate-900/90 backdrop-blur-md border border-slate-700 px-3 py-1.5 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap shadow-xl pointer-events-none">
        <span className="text-xs font-bold text-white">{label}</span>
      </div>
    </div>
  </div>
);

// Edit Vehicle Modal Component
function EditVehicleModal({ vehicle, onClose, onSuccess }) {
  const { isDark } = useTheme();
  const [formData, setFormData] = useState({
    vehicle_name: vehicle.vehicle_name || '',
    year: vehicle.year || '',
    make: vehicle.make || '',
    model: vehicle.model || '',
    trim: vehicle.trim || '',
    color: vehicle.color || '',
    license_plate: vehicle.license_plate || '',
    vin: vehicle.vin || '',
    status: vehicle.status || 'Active',
    notes: vehicle.notes || '',
    vehicle_image_url: vehicle.vehicle_image_url || ''
  });
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(vehicle.vehicle_image_url || null);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        alert('Please select an image file');
        return;
      }
      if (file.size > 10 * 1024 * 1024) { // 10MB limit
        alert('Image must be less than 10MB');
        return;
      }
      setImageFile(file);
      setImagePreview(URL.createObjectURL(file));
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      let imageUrl = formData.vehicle_image_url;

      // Upload image if a new one was selected
      if (imageFile) {
        setUploading(true);
        try {
          const uploadFormData = new FormData();
          uploadFormData.append('files', imageFile);
          uploadFormData.append('category', 'vehicle-image');
          uploadFormData.append('isPublic', 'true');

          const uploadResult = await api.upload('/api/files/upload', uploadFormData);

          if (uploadResult.files && uploadResult.files.length > 0) {
            // Save the GCS path instead of signed URL (which expires)
            // The backend will generate fresh signed URLs when returning vehicles
            imageUrl = uploadResult.files[0].gcs_path || uploadResult.files[0].gcs_url || uploadResult.files[0].viewUrl;
          }
        } catch (uploadErr) {
          console.error('Image upload error:', uploadErr);
          alert('Failed to upload image. Saving vehicle without image update.');
        }
        setUploading(false);
      }

      // Update vehicle - only send non-empty values
      const updateData = {};
      Object.keys(formData).forEach(key => {
        const value = formData[key];
        // Only include non-empty values, or explicitly include empty strings for text fields
        if (value !== '' && value !== null && value !== undefined) {
          updateData[key] = value;
        } else if (key === 'notes' || key === 'vehicle_name') {
          // Allow empty strings for text fields
          updateData[key] = value || null;
        }
      });
      updateData.vehicle_image_url = imageUrl;

      await api.put(`/api/vehicles/${vehicle.id}`, updateData);

      onSuccess();
    } catch (err) {
      console.error('Failed to update vehicle:', err);
      alert('Failed to update vehicle. Please try again.');
    } finally {
      setSaving(false);
      setUploading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
      <div className={`w-full max-w-3xl max-h-[90vh] overflow-y-auto rounded-2xl shadow-2xl border ${
        isDark ? 'bg-slate-900 border-slate-700' : 'bg-white border-gray-200'
      }`}>
        {/* Header */}
        <div className="sticky top-0 bg-slate-900 border-b border-slate-700 p-6 flex justify-between items-center z-10">
          <h2 className="text-2xl font-bold text-white">Edit Vehicle</h2>
          <button 
            onClick={onClose}
            className="p-2 hover:bg-slate-800 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-slate-400" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Image Upload Section */}
          <div className="space-y-4">
            <label className="block text-sm font-semibold text-slate-300">Vehicle Image</label>
            
            {/* Image Preview */}
            <div className="relative w-full h-48 bg-slate-800 rounded-xl border-2 border-dashed border-slate-700 overflow-hidden">
              {imagePreview ? (
                <img 
                  src={imagePreview} 
                  alt="Vehicle preview" 
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="flex flex-col items-center justify-center h-full text-slate-500">
                  <ImageIcon className="w-12 h-12 mb-2" />
                  <p className="text-sm">No image uploaded</p>
                </div>
              )}
            </div>

            {/* Upload Button */}
            <div className="flex gap-3">
              <label className="flex-1 cursor-pointer">
                <div className="flex items-center justify-center gap-2 px-4 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-semibold transition-colors">
                  <Upload className="w-4 h-4" />
                  {imageFile ? 'Change Image' : 'Upload Image'}
                </div>
                <input 
                  type="file" 
                  accept="image/*"
                  onChange={handleImageSelect}
                  className="hidden"
                />
              </label>
              {imagePreview && (
                <button
                  onClick={() => {
                    setImageFile(null);
                    setImagePreview(null);
                    setFormData({...formData, vehicle_image_url: ''});
                  }}
                  className="px-4 py-3 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-xl font-semibold transition-colors"
                >
                  Remove
                </button>
              )}
            </div>
            {imageFile && (
              <p className="text-sm text-slate-400">
                Selected: {imageFile.name} ({(imageFile.size / 1024 / 1024).toFixed(2)} MB)
              </p>
            )}
          </div>

          {/* Form Fields */}
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <label className="block text-sm font-semibold mb-2 text-slate-300">Vehicle Name</label>
              <input
                type="text"
                value={formData.vehicle_name}
                onChange={(e) => setFormData({...formData, vehicle_name: e.target.value})}
                placeholder="e.g., Fleet Truck #1"
                className="w-full px-4 py-3 rounded-xl border bg-slate-900 border-slate-700 text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold mb-2 text-slate-300">Year</label>
              <input
                type="number"
                value={formData.year}
                onChange={(e) => setFormData({...formData, year: e.target.value})}
                className="w-full px-4 py-3 rounded-xl border bg-slate-900 border-slate-700 text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold mb-2 text-slate-300">Make</label>
              <input
                type="text"
                value={formData.make}
                onChange={(e) => setFormData({...formData, make: e.target.value})}
                className="w-full px-4 py-3 rounded-xl border bg-slate-900 border-slate-700 text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold mb-2 text-slate-300">Model</label>
              <input
                type="text"
                value={formData.model}
                onChange={(e) => setFormData({...formData, model: e.target.value})}
                className="w-full px-4 py-3 rounded-xl border bg-slate-900 border-slate-700 text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold mb-2 text-slate-300">Trim</label>
              <input
                type="text"
                value={formData.trim}
                onChange={(e) => setFormData({...formData, trim: e.target.value})}
                className="w-full px-4 py-3 rounded-xl border bg-slate-900 border-slate-700 text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold mb-2 text-slate-300">Color</label>
              <input
                type="text"
                value={formData.color}
                onChange={(e) => setFormData({...formData, color: e.target.value})}
                className="w-full px-4 py-3 rounded-xl border bg-slate-900 border-slate-700 text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold mb-2 text-slate-300">License Plate</label>
              <input
                type="text"
                value={formData.license_plate}
                onChange={(e) => setFormData({...formData, license_plate: e.target.value})}
                className="w-full px-4 py-3 rounded-xl border bg-slate-900 border-slate-700 text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold mb-2 text-slate-300">VIN</label>
              <input
                type="text"
                value={formData.vin}
                onChange={(e) => setFormData({...formData, vin: e.target.value})}
                className="w-full px-4 py-3 rounded-xl border bg-slate-900 border-slate-700 text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold mb-2 text-slate-300">Status</label>
              <select
                value={formData.status}
                onChange={(e) => setFormData({...formData, status: e.target.value})}
                className="w-full px-4 py-3 rounded-xl border bg-slate-900 border-slate-700 text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50"
              >
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
                <option value="In Repair">In Repair</option>
              </select>
            </div>

            <div className="col-span-2">
              <label className="block text-sm font-semibold mb-2 text-slate-300">Notes</label>
              <textarea
                value={formData.notes}
                onChange={(e) => setFormData({...formData, notes: e.target.value})}
                rows={3}
                className="w-full px-4 py-3 rounded-xl border bg-slate-900 border-slate-700 text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50 resize-none"
              />
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-slate-900 border-t border-slate-700 p-6 flex justify-end gap-3">
          <button
            onClick={onClose}
            disabled={saving || uploading}
            className="px-6 py-3 rounded-xl border border-slate-700 text-slate-300 hover:bg-slate-800 font-semibold transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving || uploading}
            className="px-6 py-3 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-semibold transition-colors disabled:opacity-50 flex items-center gap-2"
          >
            {uploading && <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>}
            {uploading ? 'Uploading...' : saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
}
